<template>
    <div class="guide-code-tabs" :class="{ 'guide-code-tabs--single': availableLanguages.length === 1 }">
        <q-tabs
            v-if="availableLanguages.length > 1"
            v-model="activeTab"
            dense
            inline-label
            align="left"
            narrow-indicator
            indicator-color="teal-4"
            active-color="teal-2"
            class="guide-code-tabs__tabs"
        >
            <q-tab
                v-for="lang in availableLanguages"
                :key="lang.key"
                :name="lang.key"
                :label="lang.label"
                no-caps
            />
        </q-tabs>

        <div class="guide-code-tabs__panel">
            <q-btn
                dense
                flat
                round
                size="sm"
                icon="content_copy"
                color="grey-5"
                class="guide-code-tabs__copy-btn"
                :title="copied ? 'Copied!' : 'Copy to clipboard'"
                @click="copyActive"
            >
                <q-tooltip v-if="copied" anchor="top middle" self="bottom middle" class="bg-teal text-white">
                    Copied
                </q-tooltip>
            </q-btn>

            <pre class="code-block" :class="`code-block--${activeLangMeta.prismLang}`"><code :ref="setCodeRef" :class="`language-${activeLangMeta.prismLang}`">{{ activeSample }}</code></pre>
        </div>
    </div>
</template>

<script>
// IMPORTANT: prism-bootstrap must be imported BEFORE the Prism language components so that
// `window.Prism` is set (and auto-highlight is disabled) before each component file evaluates.
// ES module bodies execute in dependency-declaration order, so this ordering is significant.
//
// Language dependency notes:
//   - `prismjs` core ships markup, css, clike, and javascript built in - no import needed.
//   - `prism-markup-templating` must load BEFORE `prism-php` because PHP's grammar calls
//     Prism.languages['markup-templating'].tokenizePlaceholders at tokenize time.
//   - csharp and ruby both extend clike, which is already in core.
import Prism from './prism-bootstrap.js';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-http';

// Canonical tab order. Keys match the property names consumers use in the `samples` prop.
// `prismLang` maps to the Prism grammar we load above; 'none' disables highlighting.
const LANGUAGES = [
    { key: 'curl',     label: 'cURL',       prismLang: 'bash' },
    { key: 'js',       label: 'JavaScript', prismLang: 'javascript' },
    { key: 'node',     label: 'Node.js',    prismLang: 'javascript' },
    { key: 'python',   label: 'Python',     prismLang: 'python' },
    { key: 'csharp',   label: 'C#',         prismLang: 'csharp' },
    { key: 'php',      label: 'PHP',        prismLang: 'php' },
    { key: 'ruby',     label: 'Ruby',       prismLang: 'ruby' },
    { key: 'bash',     label: 'Bash',       prismLang: 'bash' },
    { key: 'json',     label: 'JSON',       prismLang: 'json' },
    { key: 'http',     label: 'HTTP',       prismLang: 'http' },
    // Markdown renders without Prism highlighting because the grammar is not loaded
    // above. The primer block on the AI Agents guide is plain markdown meant to be
    // copy-pasted verbatim into an AI agent's memory, so no highlighting is desired.
    { key: 'markdown', label: 'Markdown',   prismLang: 'none' },
    { key: 'text',     label: 'Text',       prismLang: 'none' }
];

export default {
    name: 'GuideCodeTabs',

    props: {
        // Required. An object whose keys are language identifiers (curl, js, node, python,
        // csharp, php, ruby, json, http, text) and whose values are the code sample strings.
        // Keys not present in this object are simply not rendered as tabs.
        //
        // Pass multi-line strings as JS template literals so newlines and indentation survive
        // Vue's template compiler exactly as written:
        //
        //   :samples="{
        //       curl: `curl -X POST ...
        //         -H 'Authorization: Bearer ...'`,
        //       js: `await fetch(...)`
        //   }"
        samples: {
            type: Object,
            required: true
        },
        // Which tab to show first. Defaults to cURL when available, otherwise the first key.
        defaultLang: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            activeTab: this.pickDefaultTab(),
            codeEl: null,
            copied: false,
            copyResetTimer: null
        };
    },

    computed: {
        availableLanguages() {
            return LANGUAGES.filter((l) => {
                const v = this.samples[l.key];
                return typeof v === 'string' && v.length > 0;
            });
        },

        activeLangMeta() {
            return LANGUAGES.find((l) => l.key === this.activeTab) || this.availableLanguages[0] || LANGUAGES[0];
        },

        activeSample() {
            return this.samples[this.activeTab] || '';
        }
    },

    watch: {
        activeTab() {
            this.$nextTick(() => this.highlight());
        },
        samples: {
            handler() {
                // Re-pick default if the currently active tab disappeared.
                if (!this.availableLanguages.find((l) => l.key === this.activeTab)) {
                    this.activeTab = this.pickDefaultTab();
                }
                this.$nextTick(() => this.highlight());
            },
            deep: false
        }
    },

    mounted() {
        this.$nextTick(() => this.highlight());
    },

    beforeUnmount() {
        if (this.copyResetTimer) {
            clearTimeout(this.copyResetTimer);
        }
    },

    methods: {
        pickDefaultTab() {
            const available = LANGUAGES.filter((l) => {
                const v = this.samples && this.samples[l.key];
                return typeof v === 'string' && v.length > 0;
            });
            if (available.length === 0) return null;

            const requested = this.defaultLang;
            if (requested && available.find((l) => l.key === requested)) return requested;

            const curl = available.find((l) => l.key === 'curl');
            return curl ? curl.key : available[0].key;
        },

        setCodeRef(el) {
            this.codeEl = el;
        },

        highlight() {
            if (!this.codeEl) return;
            const lang = this.activeLangMeta.prismLang;
            if (lang === 'none') return;
            if (!Prism.languages[lang]) {
                // Grammar didn't load - bail rather than throw. Content still shows as plain text.
                return;
            }
            Prism.highlightElement(this.codeEl);
        },

        copyActive() {
            const text = this.activeSample;
            if (!text) return;
            const done = () => {
                this.copied = true;
                if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
                this.copyResetTimer = setTimeout(() => { this.copied = false; }, 1500);
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done));
            } else {
                this.fallbackCopy(text, done);
            }
        },

        fallbackCopy(text, done) {
            // Legacy clipboard fallback for older browsers / non-secure contexts.
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.position = 'absolute';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); done(); } catch (_) { /* ignore */ }
            document.body.removeChild(ta);
        }
    }
};
</script>

<style>
/* Dark theme that matches the existing .code-block styling. Prism token colors layered on top.
   Unscoped so that token colors apply to Prism-generated <span class="token ..."> elements. */

.guide-code-tabs {
    margin: 16px 0;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #2f3a4b;
}

.guide-code-tabs__tabs {
    background: #0f172a;
    color: #94a3b8;
    border-bottom: 1px solid #2f3a4b;
    min-height: 34px;
}

.guide-code-tabs__tabs .q-tab {
    min-height: 34px;
    padding: 0 14px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.02em;
    text-transform: none;
}

.guide-code-tabs__tabs .q-tab--active {
    color: #99f6e4;
}

.guide-code-tabs__panel {
    position: relative;
    background: #1e293b;
}

/* pre + code styling: match the existing .code-block look but force whitespace preservation
   so multi-line code renders with its original indentation. */
.guide-code-tabs__panel .code-block {
    margin: 0;
    border-radius: 0;
    background: #1e293b;
    color: #e2e8f0;
    padding: 14px 18px;
    overflow-x: auto;
    font-size: 13px;
    line-height: 1.6;
    font-family: 'Cascadia Code', 'Fira Code', 'JetBrains Mono', 'Consolas', monospace;
    white-space: pre;
}

.guide-code-tabs__panel .code-block code {
    background: transparent !important;
    color: inherit !important;
    padding: 0 !important;
    font-size: inherit !important;
    white-space: pre;
    display: block;
}

.guide-code-tabs__copy-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    opacity: 0.55;
    transition: opacity 120ms ease-out;
}

.guide-code-tabs__copy-btn:hover {
    opacity: 1;
}

/* Prism token colors - minimal custom palette matching the slate background. */
.guide-code-tabs__panel .token.comment,
.guide-code-tabs__panel .token.prolog,
.guide-code-tabs__panel .token.doctype,
.guide-code-tabs__panel .token.cdata {
    color: #64748b;
    font-style: italic;
}

.guide-code-tabs__panel .token.punctuation {
    color: #cbd5e1;
}

.guide-code-tabs__panel .token.property,
.guide-code-tabs__panel .token.tag,
.guide-code-tabs__panel .token.constant,
.guide-code-tabs__panel .token.symbol,
.guide-code-tabs__panel .token.deleted {
    color: #f472b6;
}

.guide-code-tabs__panel .token.boolean,
.guide-code-tabs__panel .token.number {
    color: #fb923c;
}

.guide-code-tabs__panel .token.selector,
.guide-code-tabs__panel .token.attr-name,
.guide-code-tabs__panel .token.string,
.guide-code-tabs__panel .token.char,
.guide-code-tabs__panel .token.builtin,
.guide-code-tabs__panel .token.inserted {
    color: #86efac;
}

.guide-code-tabs__panel .token.operator,
.guide-code-tabs__panel .token.entity,
.guide-code-tabs__panel .token.url,
.guide-code-tabs__panel .language-css .token.string,
.guide-code-tabs__panel .style .token.string {
    color: #fcd34d;
}

.guide-code-tabs__panel .token.atrule,
.guide-code-tabs__panel .token.attr-value,
.guide-code-tabs__panel .token.keyword {
    color: #93c5fd;
}

.guide-code-tabs__panel .token.function,
.guide-code-tabs__panel .token.class-name {
    color: #5eead4;
}

.guide-code-tabs__panel .token.regex,
.guide-code-tabs__panel .token.important,
.guide-code-tabs__panel .token.variable {
    color: #fcd34d;
}

.guide-code-tabs__panel .token.important,
.guide-code-tabs__panel .token.bold {
    font-weight: 700;
}

.guide-code-tabs__panel .token.italic {
    font-style: italic;
}
</style>
